// HeroSplash - Full-viewport hero with city search for the homepage
import _ from "lodash";
import "./hero-splash.scss";
import { useEffect, useRef, useState } from "react";
import InputGroupControl from "../Common/InputGroup";
import LocationSearchDropdown from "../Common/LocationSearchDropdown";
import HowItWorksSection from "./HowItWorksSection";
import { capitalizeString } from "../../actions/common";
import { getGeoLocation } from "../../services/commonServices";
import {
  preloadPlaces,
  getAutocompletePredictions,
} from "../../services/placesService";
import {
  getLocalStorage,
  getUserId,
  removeLocalStorage,
} from "../../helpers/common";
import { useCurrentWebsite } from "../../hooks/useCurrentWebsite";
import useTenantLocations from "../../hooks/useTenantLocations";
import useAvailableCities from "../../hooks/useAvailableCities";

const HeroSplash = ({
  setUserLocation,
  adType,
  onSearchStart,
}) => {
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const { searchCities, isLocationAllowed } = useTenantLocations();
  const { featuredCities, filterCities, allCitiesLoaded } = useAvailableCities();
  const [locationList, setLocationList] = useState([]);
  const [availableMarketResults, setAvailableMarketResults] = useState([]);
  const [location, setLocation] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const blurTimerRef = useRef(null);
  const latestRequestRef = useRef(0);
  const userLocation = getLocalStorage("userLocation");
  const locationString = getLocalStorage("LocationString");
  const userId = getUserId();

  // Preload Places script on mount so it's ready when user starts typing
  useEffect(() => {
    if (isGitchaDefaultWebsite) {
      preloadPlaces();
    }
  }, [isGitchaDefaultWebsite]);

  useEffect(() => {
    if (locationString != null && userId) {
      const reqPayload = `address=${capitalizeString(
        locationString
      )}&components=country:US`;
      getAddresseDetails({ reqPayload });
    }
  }, [locationString, userId]);

  // Show featured cities on focus when input is empty
  const handleFocus = () => {
    // A pending hide from a previous blur would close the list we just opened.
    clearTimeout(blurTimerRef.current);
    setIsFocused(true);
    if (!location || location.trim() === "") {
      setAvailableMarketResults(featuredCities);
    }
  };

  const handleBlur = () => {
    clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      setIsFocused(false);
      if (!location || location.trim() === "") {
        setAvailableMarketResults([]);
        setLocationList([]);
      }
    }, 200);
  };

  useEffect(() => () => clearTimeout(blurTimerRef.current), []);

  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    if (value && value.trim() !== "") {
      setLocation(value);

      // Tenant-restricted sites search against allowed city list
      if (!isGitchaDefaultWebsite) {
        const isZipCode = /^\d{3,5}$/.test(value.trim());
        if (isZipCode) {
          handleTenantZipSearch(value.trim());
        } else {
          const results = searchCities(value.trim());
          setLocationList(
            results.slice(0, 5).map((c) => ({
              _isTenantCity: true,
              city: c.city,
              state: c.state,
              formatted_address: c.displayName,
            })),
          );
        }
        setAvailableMarketResults([]);
        return;
      }

      // Filter available Gitcha cities
      const filtered = filterCities(value.trim());
      setAvailableMarketResults(filtered.slice(0, 3));

      const reqPayload = `address=${value}&components=country:US`;
      getAddresseDetails({ reqPayload, inputValue: val.trim() });
    } else {
      clearAddressList();
      clearLocationStates();
      // Show featured again when input cleared while focused
      if (isFocused) {
        setAvailableMarketResults(featuredCities);
      }
    }
  };

  // ZIP search for tenant-restricted sites
  const handleTenantZipSearch = async (zip) => {
    const requestId = ++latestRequestRef.current;
    const reqPayload = `address=${zip}&components=country:US`;
    const geoData = await getGeoLocation(reqPayload);
    const results = _.get(geoData, "results", []);
    const validResults = [];
    for (const result of results) {
      const components = _.get(result, "address_components", []);
      const locality = components.find((c) => c.types?.includes("locality"));
      const stateComp = components.find((c) =>
        c.types?.includes("administrative_area_level_1"),
      );
      const city = locality?.long_name;
      const state = stateComp?.short_name;
      if (city && state && isLocationAllowed(city, state)) {
        validResults.push({
          _isTenantCity: true,
          city,
          state,
          formatted_address: `${city}, ${state}`,
        });
      }
    }
    if (requestId !== latestRequestRef.current) return;
    setLocationList(validResults.slice(0, 5));
  };

  const clearLocationStates = () => {
    if (!locationString) {
      removeLocalStorage("searchedLocation");
      setUserLocation(userLocation);
      setLocation("");
    }
  };

  const getAddresseDetails = async ({ reqPayload, inputValue }) => {
    // Keystrokes fire unsequenced Places lookups; a late one must not swap the
    // list out from under a click that is already in flight.
    const requestId = ++latestRequestRef.current;
    const isLatestRequest = () => requestId === latestRequestRef.current;
    const startsWithNumber = inputValue && /^\d/.test(inputValue);
    const isZipCode = inputValue && /^\d{3,5}$/.test(inputValue.trim());

    // locationString auto-select uses geocoding (resolves a known location)
    if (!inputValue && locationString) {
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        const fa = _.get(results[0], "formatted_address", "");
        if (fa !== "United States") {
          handleItemSelection(results[0]);
          removeLocalStorage("LocationString");
        }
      }
      return;
    }

    if (isZipCode) {
      // ZIP code: geocoding is best for exact ZIP resolution
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      const filtered = results.filter((r) => {
        const fa = _.get(r, "formatted_address", "");
        return fa !== "United States";
      });
      if (!isLatestRequest()) return;
      setLocationList(filtered.slice(0, 5));
      return;
    }

    // Cities: Places with (cities) type ranks major cities correctly
    // Addresses: Places with address type returns street addresses
    const placesType = startsWithNumber ? ["address"] : ["(cities)"];
    const predictions = await getAutocompletePredictions(
      capitalizeString(inputValue),
      {
        types: placesType,
        componentRestrictions: { country: "us" },
      },
    );

    const items = predictions.map((p) => ({
      _isPlacesPrediction: true,
      place_id: p.place_id,
      formatted_address: p.description,
      types: p.types || [],
    }));

    if (!isLatestRequest()) return;
    setLocationList(items.slice(0, 5));
  };

  const clearAddressList = () => {
    setLocationList([]);
    clearLocationStates();
  };

  // Handle clicking an available market - geocode then use onSearchStart flow
  const handleAvailableMarketClick = async (market) => {
    const reqPayload = `address=${encodeURIComponent(market.city + ", " + market.state)}&components=country:US`;
    const geoData = await getGeoLocation(reqPayload);
    const results = _.get(geoData, "results", []);
    if (results?.length > 0) {
      // Pass through handleItemSelection which calls onSearchStart
      handleItemSelection(results[0]);
    }
  };

  // Handle clicking a dropdown item - geocoding result, Places prediction, or tenant city.
  // For Places predictions, geocode the description to get full county info
  // (Places getDetails for cities often omits administrative_area_level_2).
  const handleDropdownClick = async (item) => {
    if (item._isTenantCity) {
      // Tenant city - geocode to get lat/lng and county, then proceed
      const reqPayload = `address=${encodeURIComponent(item.city + ", " + item.state)}&components=country:US`;
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        handleItemSelection(results[0]);
      }
    } else if (item._isPlacesPrediction) {
      const reqPayload = `address=${encodeURIComponent(item.formatted_address)}&components=country:US`;
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        handleItemSelection(results[0]);
      }
    } else {
      handleItemSelection(item);
    }
  };

  const handleItemSelection = async (address) => {
    let city, state, county;
    removeLocalStorage("searchedLocation");
    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");

    const stateSearched = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }

    const locality = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "locality";
    });
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }

    const countyList = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });
    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }

    setLocation(
      city != undefined ? city + ", " + state : formatted_address
    );
    setLocationList([]);
    setAvailableMarketResults([]);

    const locationObject = {
      latitude: _.get(locBounds, "lat", ""),
      longitude: _.get(locBounds, "lng", ""),
      city: city,
      state: state,
      formatted_address:
        city != undefined ? city + ", " + state : formatted_address,
    };

    // Trigger the transition overlay - Home.js handles the delayed location check
    onSearchStart(locationObject, city, state, county, formatted_address);
  };

  const showDropdown =
    isFocused && (availableMarketResults.length > 0 || locationList.length > 0);

  return (
    <div className="hero-splash">
      <div className="hero-splash__aurora" />
      <div className="hero-splash__bg">
        <div className="hero-splash__content">
          <h1>
            See the real-time buyer demand near you.
          </h1>
          <div className="hero-splash__search">
            <div className="search_field_input input-group position-relative">
              <InputGroupControl
                inputGroupClass="form-control pr-0"
                ariaLabel="Search location"
                ariaDescribedBy="hero-search"
                placeholder="Enter your city, ZIP, or address"
                errorMessage=""
                value={location || ""}
                isIconEnable={false}
                onChange={(e) => handleLocationChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
              {showDropdown && (
                <LocationSearchDropdown
                  availableMarkets={availableMarketResults}
                  placesResults={locationList}
                  onAvailableMarketClick={handleAvailableMarketClick}
                  onPlacesResultClick={handleDropdownClick}
                  className="mw-100"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <HowItWorksSection />
    </div>
  );
};

export default HeroSplash;
